import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fieldCls = "w-full bg-[#0B132B] border border-[#1E293B] rounded-md px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#F97316] transition-colors";
const labelCls = "block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5";

export default function ContentTab() {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setForm(r.data.content)).catch(() => {});
  }, []);

  if (!form) return <div className="mt-6 text-sm text-slate-500" data-testid="content-loading">Loading...</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setWhy = (i, k, v) => {
    const arr = [...(form.why_choose_us || [])];
    arr[i] = { ...arr[i], [k]: v };
    set("why_choose_us", arr);
  };
  const setStat = (i, k, v) => {
    const arr = [...(form.stats || [])];
    arr[i] = { ...arr[i], [k]: v };
    set("stats", arr);
  };

  const save = async () => {
    try {
      await api.put("/admin/content", {
        hero_title: form.hero_title, hero_subtitle: form.hero_subtitle,
        phone: form.phone, email: form.email, address: form.address,
        about_title: form.about_title, about_text: form.about_text,
        why_choose_us: form.why_choose_us, stats: form.stats,
      });
      toast.success("Site content saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="mt-6 space-y-8" data-testid="content-tab">
      <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-6">
        <h3 className="font-display text-xl font-bold text-white mb-5">Homepage Hero</h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Hero Title</label>
            <input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className={fieldCls} data-testid="content-hero-title" />
          </div>
          <div>
            <label className={labelCls}>Hero Subtitle</label>
            <textarea value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} rows={2} className={fieldCls} data-testid="content-hero-subtitle" />
          </div>
        </div>
      </div>

      <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-6">
        <h3 className="font-display text-xl font-bold text-white mb-5">Contact Info</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className={labelCls}>Phone</label>
            <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={fieldCls} data-testid="content-phone" /></div>
          <div><label className={labelCls}>Email</label>
            <input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={fieldCls} data-testid="content-email" /></div>
          <div><label className={labelCls}>Address / Coverage</label>
            <input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={fieldCls} data-testid="content-address" /></div>
        </div>
      </div>

      <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-6">
        <h3 className="font-display text-xl font-bold text-white mb-5">About Page</h3>
        <div className="space-y-4">
          <div><label className={labelCls}>About Title</label>
            <input value={form.about_title || ""} onChange={(e) => set("about_title", e.target.value)} className={fieldCls} data-testid="content-about-title" /></div>
          <div><label className={labelCls}>About Text (blank line between paragraphs)</label>
            <textarea value={form.about_text || ""} onChange={(e) => set("about_text", e.target.value)} rows={7} className={fieldCls} data-testid="content-about-text" /></div>
        </div>
      </div>

      <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold text-white">Why Choose Us</h3>
          <button onClick={() => set("why_choose_us", [...(form.why_choose_us || []), { title: "", text: "" }])}
            data-testid="content-add-why" className="flex items-center gap-1.5 text-xs text-[#F97316] border border-[#F97316]/40 rounded-md px-3 py-1.5 hover:bg-[#F97316]/10">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="space-y-4">
          {(form.why_choose_us || []).map((w, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-start" data-testid={`content-why-${i}`}>
              <input value={w.title} onChange={(e) => setWhy(i, "title", e.target.value)} placeholder="Title" className={fieldCls} />
              <input value={w.text} onChange={(e) => setWhy(i, "text", e.target.value)} placeholder="Text" className={fieldCls} />
              <button onClick={() => set("why_choose_us", form.why_choose_us.filter((_, j) => j !== i))}
                className="p-2 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#131C38] border border-[#1E293B] rounded-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold text-white">Hero Stats</h3>
          <button onClick={() => set("stats", [...(form.stats || []), { value: "", label: "" }])}
            data-testid="content-add-stat" className="flex items-center gap-1.5 text-xs text-[#F97316] border border-[#F97316]/40 rounded-md px-3 py-1.5 hover:bg-[#F97316]/10">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {(form.stats || []).map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-center" data-testid={`content-stat-${i}`}>
              <input value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="50+" className={fieldCls} />
              <input value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Label" className={fieldCls} />
              <button onClick={() => set("stats", form.stats.filter((_, j) => j !== i))}
                className="p-2 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} data-testid="content-save-btn"
        className="bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-sm px-8 py-3.5 rounded-md transition-colors">
        Save All Content
      </button>
    </div>
  );
}
